// src/hooks/useDentallyBooking.js
import { useEffect, useRef, useState } from 'react';
import { callApi } from '../utils/apiCall';
import { toAssigneeIds } from '../utils/toAssigneeIds';
import { createDentallyAppointmentAsync } from '../utils/dentallyApi';
import { projectDentallyBookingDiagnostic } from '../utils/dentallyBookingDiagnostics';
import { buildManualLeadPayload, getCreatedManualLeadTreatment } from '../utils/buildManualLeadPayload';
import { mergeAdAttributionIntoLeadPayload, clearStoredAdAttribution } from '../utils/adAttribution';
import { normaliseDobIso, postManualLead } from '../utils/leadIdentityApi';
import { sendPaymentLink, summarisePaymentLinkResult, hasAnySuccessfulChannel } from '../redux/slices/lead/paymentLinkThunks';

export const DENTALLY_BOOKING_SUBMIT_REASONS = Object.freeze({
  STALE_SCOPE: 'stale_scope',
  PRACTICE_REQUIRED: 'practice_required',
  UNSUPPORTED_MODE: 'unsupported_mode',
  PATIENT_LINK_UNRESOLVED: 'patient_link_unresolved',
  PATIENT_DETAILS_REQUIRED: 'patient_details_required',
  DUPLICATE: 'duplicate',
  LEAD_CREATE_FAILED: 'lead_create_failed',
  IDENTITY_CANCELLED: 'identity_cancelled',
  LEAD_ID_MISSING: 'lead_id_missing',
  TREATMENT_CREATE_FAILED: 'treatment_create_failed',
  TREATMENT_REQUIRED: 'treatment_required',
  INVALID_APPOINTMENT_REASON: 'invalid_appointment_reason',
  APPOINTMENT_TIME_REQUIRED: 'appointment_time_required',
  PATIENT_DETAILS_SAVE_FAILED: 'patient_details_save_failed',
  CRM_APPOINTMENT_CREATE_FAILED: 'crm_appointment_create_failed',
  CRM_APPOINTMENT_ID_MISSING: 'crm_appointment_id_missing',
  UNEXPECTED_FAILURE: 'unexpected_failure',
});

/**
 * @typedef {'stale_scope' | 'practice_required' | 'unsupported_mode' |
 * 'patient_link_unresolved' | 'patient_details_required' | 'duplicate' |
 * 'lead_create_failed' | 'lead_id_missing' | 'treatment_create_failed' |
 * 'treatment_required' | 'invalid_appointment_reason' | 'appointment_time_required' |
 * 'patient_details_save_failed' | 'crm_appointment_create_failed' |
 * 'crm_appointment_id_missing' | 'unexpected_failure'} DentallyBookingSubmitFailureReason
 * @typedef {{ok: true} | {ok: false, reason: DentallyBookingSubmitFailureReason}} DentallyBookingSubmitOutcome
 */

/** @param {DentallyBookingSubmitFailureReason} reason */
const submitRejected = (reason) => ({ ok: false, reason });
const SUBMIT_COMPLETED = Object.freeze({ ok: true });

/**
 * Manages the sequential API calls for Dentally appointment booking.
 *
 * Normal mode (dentallyOnly=false):
 *   New lead: (1) create lead and treatment atomically → (2) resolve treatment →
 *             (3) CRM appointment → (4) Dentally appointment → (5) payment link
 *   Existing lead: retains treatment-only creation when no existing treatment is selected.
 *   existingLead: lead object if booking from lead detail page, null if new lead
 *
 * Dentally-only test mode (dentallyOnly=true):
 *   Steps: (1) create Dentally patient → (2) create Dentally appointment
 *   No CRM lead, treatment, or appointment is created.
 */
export function useDentallyBooking({
  practiceId,
  existingLead = null,
  dentallyOnly = false,
  existingTreatmentId = null,
  isPaymentRequired = false,
  patientLinkOutcome,
  operationScope,
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: true, steps: [...], leadId, ... }
  const [error, setError] = useState(null);   // { step, message }
  const [bookingStep, setBookingStep] = useState(null); // { index: 1-5, label: string }
  const [bookingDiagnostic, setBookingDiagnostic] = useState(null);
  const mountedRef = useRef(true);
  const operationSequenceRef = useRef(0);
  const activeOperationRef = useRef(null);
  const scopeKey = [practiceId, existingLead?.id, existingTreatmentId, dentallyOnly, isPaymentRequired, operationScope].join(':');
  const scopeRef = useRef(scopeKey);

  if (scopeRef.current !== scopeKey) {
    scopeRef.current = scopeKey;
    activeOperationRef.current = null;
    operationSequenceRef.current += 1;
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activeOperationRef.current = null;
      operationSequenceRef.current += 1;
    };
  }, []);

  useEffect(() => {
    setLoading(false);
    setResult(null);
    setError(null);
    setBookingStep(null);
    setBookingDiagnostic(null);
  }, [scopeKey]);

  const reset = () => {
    activeOperationRef.current = null;
    operationSequenceRef.current += 1;
    setLoading(false);
    setResult(null);
    setError(null);
    setBookingStep(null);
    setBookingDiagnostic(null);
  };

  /**
   * formData shape:
   * {
   *   // Patient (new lead only)
   *   firstName, lastName, phone, email,
   *   salutation,            // '' | 'MR' | 'MRS' | 'MISS' | 'MS' | 'DR' — canonical CRM enum
   *   dob,                   // date-only string in the API's day-first shape,
   *                          // 'DD-MM-YYYY'. NOT ISO, despite what this comment
   *                          // used to claim. Both consumers are our own API:
   *                          // the /leads/ POST below and patient_dob on the
   *                          // Dentally appointment call, whose serializer is a
   *                          // DRF DateField and lists "%d-%m-%Y" first in
   *                          // DATE_INPUT_FORMATS. The backend converts to ISO
   *                          // for Dentally itself (apps/dentally/models.py).
   *   identityResolution,    // optional: operator's identity_resolution for the lead POST
   *   persistedPatientFields, // { salutation: boolean, dob: boolean } from the authoritative scoped profile
   *                            // When present, these flags exclusively decide patient-field persistence.
   *                            // Omitted legacy submissions use existingLead salutation presence and DOB equality.
   *   postcode,              // string — required by Dentally
   *   // Treatment
   *   treatmentId,           // practice treatment ID
   *   // Appointment
   *   practitionerId,        // Dentally practitioner ID (number)
   *   reason,                // string from DENTALLY_REASON_OPTIONS
   *   dateTime,              // JS Date — used as CRM datetime AND Dentally start_time
   *   durationMinutes,       // number (15/30/45/60)
   *   method,                // 'IN_PRACTICE' | 'VIDEO_CALL' | 'PHONE'
   *   assignees,             // array of practice user IDs
   *   notes,                 // string (optional)
   *   // Payment
   *   paymentOption,         // 'none' | 'received' | 'link'
   *   paymentAmount,         // number in pounds e.g. 50.00 — required if 'received' or 'link'
   *   paymentType,           // 'DEPOSIT' | 'CONSULTATION' | 'TREATMENT' — required if not 'none'
   *   receivedMode,          // 'PHONE' | 'INPRACTICE' | 'BANK_TRANSFER' | 'OTHER' — only if paymentOption === 'received'
   *   paymentLinkChannel,    // 'email' | 'whatsapp' | 'both' — delivery channel for payment link (default: 'email')
   * }
   */
  /**
   * Resolves a closed, non-throwing outcome for caller coordination. Failure
   * reasons are stable enums and never contain API response or exception text.
   *
   * @returns {Promise<DentallyBookingSubmitOutcome>}
   */
  const submit = async (formData) => {
    // A callback from a prior render must never acquire ownership of the new scope.
    if (scopeRef.current !== scopeKey) {
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.STALE_SCOPE);
    }
    if (!practiceId) {
      setError({ step: 0, message: 'Practice not loaded. Please try again.' });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.PRACTICE_REQUIRED);
    }
    if (dentallyOnly) {
      setError({
        step: 0,
        message: 'Dentally-only booking is unavailable because a saved CRM appointment is required for safe booking and cancellation.',
      });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.UNSUPPORTED_MODE);
    }
    const resolvedSalutation = formData.salutation || existingLead?.salutation;
    // Writes pass the form value through untouched: the form emits day-first
    // ('DD-MM-YYYY'), which our DRF DateField accepts and the backend converts for
    // Dentally. Only COMPARISONS go via ISO, so 'DD-MM-YYYY' vs 'YYYY-MM-DD' vs
    // Date never reads as a false change.
    const bookingDob = formData.dob;
    const existingDob = existingLead?.dob;
    const sameDob = (a, b) => (normaliseDobIso(a) || a) === (normaliseDobIso(b) || b);
    const resolvedDob = bookingDob || existingLead?.dob;
    const unsafeLinkOutcome = ['error', 'malformed', 'ambiguous', 'loading'].includes(patientLinkOutcome);
    if (existingLead && unsafeLinkOutcome) {
      setError({
        step: 0,
        message: 'Unable to verify the Dentally patient link. Please try again.',
      });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.PATIENT_LINK_UNRESOLVED);
    }
    if ((!existingLead || patientLinkOutcome !== 'unique') && (!resolvedSalutation || !resolvedDob)) {
      setError({
        step: 0,
        message: existingLead && patientLinkOutcome !== 'none'
          ? 'Unable to verify the Dentally patient link. Please try again.'
          : 'Salutation and date of birth are required before booking an unlinked Dentally patient.',
      });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.PATIENT_DETAILS_REQUIRED);
    }
    if (!formData.reason || !formData.reason.trim()) {
      setError({ step: 0, message: 'Appointment reason is required before booking.' });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.INVALID_APPOINTMENT_REASON);
    }
    if (!existingLead && !formData.treatmentName) {
      setError({ step: 0, message: 'A treatment is required to complete the booking. Please select a treatment and try again.' });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.TREATMENT_REQUIRED);
    }
    if (activeOperationRef.current !== null) {
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.DUPLICATE);
    }
    operationSequenceRef.current += 1;
    const operation = { id: operationSequenceRef.current };
    activeOperationRef.current = operation;
    const operationScope = scopeRef.current;
    const isCurrentOperation = () => (
      mountedRef.current
      && scopeRef.current === operationScope
      && activeOperationRef.current === operation
    );
    const awaitCurrent = async (promise) => {
      const value = await promise;
      if (!isCurrentOperation()) throw operation;
      return value;
    };
    setLoading(true);
    setError(null);
    setResult(null);
    setBookingDiagnostic(null);

    const steps = [];  // Tracks which steps succeeded for the success screen
    const isDeferred = isPaymentRequired && formData.paymentOption === 'link';

    try {
      // ── Step 1: Create lead (new lead only) ────────────────────────────
      let leadId = existingLead != null ? existingLead.id : null;
      let createdTreatmentRecordId = null;
      if (!existingLead) {
        setBookingStep({ index: 1, label: 'Creating lead…' });
        const treatmentDetail = {
          treatment: formData.treatmentName,
          status: 'NEW',
          is_practice_notification_required: false,
        };
        // Mirrors the standard createNewLead path (lead/coreThunks.js): spread
        // everything the form collected, THEN merge stored ad attribution.
        // Previously this hand-picked ten fields, so lead_source, referral,
        // affiliate_user, every gclid/UTM parameter, sex, is_registered_patient,
        // preferred_contact_method and address lines 2/city/county were dropped
        // on Dentally practices only — invisibly, with a 201 returned.
        // Spreading rather than listing is the point: a new form field must not
        // need a second edit here to survive.
        const collected = formData.leadDetail || {};
        const leadPayload = mergeAdAttributionIntoLeadPayload(buildManualLeadPayload({
          ...collected,
          first_name: formData.firstName,
          last_name: formData.lastName,
          salutation: formData.salutation?.toUpperCase(),
          dob: bookingDob,
          suppress_initial_message_sequence: true,
          lead_contact: { ...(collected.lead_contact || {}), phone: formData.phone, email: formData.email },
          lead_address: {
            ...(collected.lead_address || {}),
            postcode: formData.postcode,
            address_line_1: formData.addressLine1,
          },
          ...(formData.nhs_number ? { nhs_number: formData.nhs_number } : {}),
          ...(formData.emergencyContactName ? { emergency_contact_name: formData.emergencyContactName } : {}),
          ...(formData.emergencyContactPhone ? { emergency_contact_phone: formData.emergencyContactPhone } : {}),
        }, treatmentDetail));
        const { response: r, cancelled } = await awaitCurrent(
          postManualLead(practiceId, leadPayload, undefined, formData.identityResolution || null),
        );
        if (cancelled) {
          setError({ step: 1, message: 'Booking stopped — no lead was created.' });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.IDENTITY_CANCELLED);
        }
        if (!r.ok) {
          console.error('[DentallyBooking] Step 1 – create lead failed', r.status);
          // DELIBERATELY a fixed string, NOT the server's reason. DRF echoes
          // submitted values back in validation errors, so `detail` can carry
          // patient-identifying content. tests/unit/hooks/
          // useDentallyBooking.durable.test.jsx pins this with a
          // 'PRIVATE-PATIENT-DETAIL' fixture; the backend client takes the same
          // line, logging 4xx status codes only for the same reason.
          setError({ step: 1, message: 'Failed to create lead.' });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.LEAD_CREATE_FAILED);
        }
        leadId = r.data.id;
        // Match the standard path: stop the same gclid/UTMs being stamped onto
        // every subsequent lead created in this session.
        clearStoredAdAttribution();
        steps.push('lead_created');
        try {
          createdTreatmentRecordId = getCreatedManualLeadTreatment(r.data, treatmentDetail).id;
          steps.push('treatment_created');
        } catch (treatmentError) {
          setError({
            step: 2,
            message: treatmentError.message,
            leadId: treatmentError.leadId || leadId,
          });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.TREATMENT_CREATE_FAILED);
        }
      }

      if (leadId == null) {
        setError({ step: 1, message: 'Lead ID is missing. Cannot continue.' });
        return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.LEAD_ID_MISSING);
      }

      // ── Step 2: Add treatment ───────────────────────────────────────────
      setBookingStep({ index: 2, label: 'Adding treatment…' });
      let treatmentRecordId;
      if (!existingLead) {
        treatmentRecordId = createdTreatmentRecordId;
      } else if (existingTreatmentId) {
        treatmentRecordId = existingTreatmentId;
        steps.push('treatment_created');
      } else if (formData.treatmentName) {
        const tRes = await awaitCurrent(callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`, 'post', {
          treatment: formData.treatmentName,
          status: 'NEW',
          // Suppress "new lead" email notification — the lead is being booked straight into consultation
          is_practice_notification_required: false,
        }));
        if (!tRes.ok) {
          console.error('[DentallyBooking] Step 2 – add treatment failed', tRes.status);
          setError({ step: 2, message: 'Failed to add treatment.' });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.TREATMENT_CREATE_FAILED);
        }
        treatmentRecordId = tRes.data.id;
        steps.push('treatment_created');
      }

      // Guard: if no treatment could be resolved, abort before creating appointments or payments
      if (!treatmentRecordId && !existingTreatmentId) {
        console.error('[DentallyBooking] Step 2 – no treatment record could be created or found');
        setError({ step: 2, message: 'A treatment is required to complete the booking. Please select or create a treatment and try again.' });
        return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.TREATMENT_REQUIRED);
      }
      const startIso = formData.dateTime instanceof Date
        ? formData.dateTime.toISOString()
        : formData.dateTime;

      if (!startIso) {
        setError({ step: 3, message: 'Appointment date and time is required.' });
        return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.APPOINTMENT_TIME_REQUIRED);
      }

      // Persist only CRM-supported patient fields before any appointment write.
      // An unlinked Dentally patient cannot be prepared safely unless these values
      // are durable on the lead that the backend will export.
      if (existingLead && leadId) {
        const missingPatientFields = {};
        const patientFieldProvenance = formData.persistedPatientFields;
        const salutationPersisted = patientFieldProvenance == null
          ? Boolean(existingLead.salutation)
          : patientFieldProvenance.salutation === true;
        const dobPersisted = patientFieldProvenance == null
          ? sameDob(bookingDob, existingDob)
          : patientFieldProvenance.dob === true;
        if (
          formData.salutation
          && !salutationPersisted
        ) {
          missingPatientFields.salutation = String(formData.salutation).toUpperCase();
        }
        if (
          formData.dob
          && !dobPersisted
        ) {
          missingPatientFields.dob = bookingDob;
        }
        if (formData.nhs_number && !existingLead.nhs_number) {
          missingPatientFields.nhs_number = formData.nhs_number;
        }
        if (formData.emergencyContactName && !existingLead.emergency_contact_name) {
          missingPatientFields.emergency_contact_name = formData.emergencyContactName;
        }
        if (formData.emergencyContactPhone && !existingLead.emergency_contact_phone) {
          missingPatientFields.emergency_contact_phone = formData.emergencyContactPhone;
        }
        if (Object.keys(missingPatientFields).length > 0) {
          const patientPatchRes = await awaitCurrent(callApi(
            `/api/v1/practices/${practiceId}/leads/${leadId}/`,
            'patch',
            missingPatientFields,
          ));
          if (!patientPatchRes?.ok) {
            setError({
              step: 3,
              message: 'Could not save the patient details required for Dentally. Please review them and try again.',
            });
            return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.PATIENT_DETAILS_SAVE_FAILED);
          }
        }
      }

      let crmAppointmentId = null;
      if (!isDeferred) {
        // ── Step 3: CRM appointment ─────────────────────────────────────────
        setBookingStep({ index: 3, label: 'Creating appointment…' });
        const aRes = await awaitCurrent(callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/`, 'post', {
          datetime: startIso,
          method: formData.method,
          type: 'CONSULTATION',
          contacted_via: 'PHONE', // how the booking was taken — always phone for Dentally bookings
          lead_treatment: treatmentRecordId,
          assignees: toAssigneeIds(formData.assignees),
        }));
        if (!aRes.ok) {
          console.error('[DentallyBooking] Step 3 – CRM appointment failed', aRes.status);
          setError({ step: 3, message: 'Failed to create CRM appointment.' });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.CRM_APPOINTMENT_CREATE_FAILED);
        }
        crmAppointmentId = aRes.data?.id;
        if (!crmAppointmentId) {
          setError({ step: 3, message: 'CRM appointment was saved but no appointment ID was returned. Dentally was not contacted.' });
          return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.CRM_APPOINTMENT_ID_MISSING);
        }
        steps.push('crm_appointment_created');
      }

      let dentallyWarning = null;
      let dentallyOutcome = null;
      const finishTime = new Date(
        new Date(startIso).getTime() + (formData.durationMinutes || 30) * 60000
      ).toISOString();
      if (!isDeferred) {
        // ── Step 4: Dentally appointment ────────────────────────────────────
        setBookingStep({ index: 4, label: 'Booking in Dentally…' });

        const dentallyExtraFields = {
          ...(bookingDob && !sameDob(bookingDob, existingDob) ? { patient_dob: bookingDob } : {}),
        };
        const dRes = await awaitCurrent(createDentallyAppointmentAsync(practiceId, {
          lead_id: leadId,
          crm_appointment_id: crmAppointmentId,
          start_time: startIso,
          finish_time: finishTime,
          practitioner_id: formData.practitionerId,
          reason: formData.reason,
          ...(formData.notes ? { notes: formData.notes } : {}),
          ...dentallyExtraFields,
        }));
        const projectedDiagnostic = projectDentallyBookingDiagnostic(dRes?.data?.diagnostic);
        if (!dRes.ok) {
          // CRM appointment is already saved — do NOT block; show amber warning
          console.error('[DentallyBooking] Step 4 – Dentally appointment failed', dRes.status);
          dentallyWarning = 'Could not sync appointment to Dentally.';
          dentallyOutcome = projectedDiagnostic?.outcome || 'reconciliation_required';
          setBookingDiagnostic(projectedDiagnostic);
        } else {
          steps.push('dentally_appointment_created');
          dentallyOutcome = dRes.data?.outcome || 'created';
          setBookingDiagnostic(null);
        }
      }

      // ── Step 5: Payment (optional) ──────────────────────────────────────
      let paymentWarning = null;
      let paymentRecordId = null;
      let checkoutUrl = null;
      let channelResults = null;
      if (formData.paymentOption === 'received' || formData.paymentOption === 'link') {
        setBookingStep({ index: 5, label: 'Processing payment…' });
        if (formData.paymentOption === 'received') {
          const paymentBody = {
            payment_type: formData.paymentType || 'DEPOSIT',
            mode_of_payment: formData.receivedMode || 'PHONE',
            amount: formData.paymentAmount,
            paid_at: new Date().toISOString(),
            // Contract: Payment create accepts an optional `appointment` id —
            // the CRM appointment just created in Step 3 above is the one
            // this payment is for.
            ...(crmAppointmentId != null ? { appointment: crmAppointmentId } : {}),
          };
          const p5Res = await awaitCurrent(callApi(
            `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentRecordId}/payments/`,
            'post',
            paymentBody,
          ));
          if (!p5Res.ok) {
            console.error('[DentallyBooking] Step 5 – payment record failed', p5Res.status);
            paymentWarning = 'Failed to record payment.';
          } else {
            paymentRecordId = p5Res.data?.id ?? null;
            steps.push('payment_received');
          }
        } else {
          // 'link' — single command call (contract A). Server persists the
          // recipient email/phone, creates the payment + checkout, and sends
          // through each requested channel (Email via Mailgun, WhatsApp via the
          // WABA template — no client-side wa.me popup, no separate contact PATCH).
          const channelMap = { email: ['email'], whatsapp: ['whatsapp'], both: ['email', 'whatsapp'] };
          const channels = channelMap[formData.paymentLinkChannel] || ['email'];
          const linkBody = {
            payment_type: formData.paymentType || 'DEPOSIT',
            amount: formData.paymentAmount,
            channels,
            ...(formData.email ? { email: formData.email } : {}),
            ...(formData.phone ? { phone: formData.phone } : {}),
            // Contract: the payment-LINK command (this endpoint) takes
            // `appointment_id` — NOT `appointment` (that's only the plain
            // payments create endpoint's field name, used just above for
            // the 'received' branch). A wrong id 400s with code
            // `appointment_invalid`.
            ...(crmAppointmentId != null ? { appointment_id: crmAppointmentId } : {}),
          };
          const p5Res = await awaitCurrent(sendPaymentLink({
            practiceId,
            leadId,
            treatmentId: treatmentRecordId,
            body: linkBody,
          }));
          channelResults = p5Res.data?.channels || null;
          if (!p5Res.ok) {
            console.error('[DentallyBooking] Step 5 – payment link command failed', p5Res.status);
            paymentWarning = p5Res.error || 'Failed to send payment link.';
          } else {
            paymentRecordId = p5Res.data?.payment_id ?? null;
            checkoutUrl = p5Res.data?.checkout_url || null;
            if (hasAnySuccessfulChannel(p5Res.data)) {
              steps.push('payment_link_sent');
            } else {
              paymentWarning = summarisePaymentLinkResult(p5Res.data) || 'Payment link could not be delivered.';
            }

            // Persist deferred booking intent on the real writable reminder/task
            // contract. Lead metadata is read-only and must never be treated as
            // durable state for this workflow.
            if (isDeferred) {
              try {
                const datetime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
                const amount = formData.paymentAmount ? `£${formData.paymentAmount}` : '';
                const pendingData = {
                  practitionerId: formData.practitionerId,
                  dateTime: startIso,
                  durationMinutes: formData.durationMinutes,
                  reason: formData.reason,
                  method: formData.method,
                  assignees: formData.assignees || [],
                  notes: formData.notes || '',
                  treatmentName: formData.treatmentName || '',
                  treatmentRecordId,
                  paymentRecordId,
                  paymentAmount: formData.paymentAmount || null,
                  savedAt: new Date().toISOString(),
                };
                const reminderRes = await awaitCurrent(callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', {
                  method: 'PAYMENT',
                  datetime,
                  note: `[DENTALLY_BOOKING_PENDING] Dentally payment pending${amount ? ` (${amount})` : ''} — lead is still NEW. Once patient pays, go to lead overview to complete the Dentally booking and move to Consultation Booked.`,
                  assignees: formData.assignees || [],
                  is_completed: false,
                  group: 'FOLLOW_UP',
                  lead: leadId,
                  metadata: { dentally_pending_booking: pendingData },
                }));
                if (!reminderRes?.ok) throw new Error('Deferred booking reminder was not saved');
                steps.push('dentally_booking_deferred');
                steps.push('dentally_reminder_created');
              } catch (reminderErr) {
                if (reminderErr === operation) throw reminderErr;
                console.warn('[DentallyBooking] Failed to create deferred reminder');
                paymentWarning = paymentWarning
                  || 'Payment link sent, but the Dentally reminder was not saved. Book in Dentally manually after payment.';
              }
            }
          }
        }
      }

      const leadName = existingLead
        ? [existingLead.first_name, existingLead.last_name].filter(Boolean).join(' ')
        : [formData.firstName, formData.lastName].filter(Boolean).join(' ');

      setResult({
        success: true,
        leadId,
        crmAppointmentId,
        leadName: leadName || null,
        treatmentRecordId,
        paymentRecordId,
        checkoutUrl,
        channelResults,
        contactEmail: formData.email || null,
        contactPhone: formData.phone || null,
        paymentLinkChannel: formData.paymentLinkChannel || 'email',
        paymentAmount: formData.paymentAmount || null,
        steps,
        isDeferred,
        dentallyWarning,
        dentallyOutcome,
        paymentWarning,
        startTime: startIso,
        finishTime,
        practitionerId: formData.practitionerId,
        reason: formData.reason,
      });
      return SUBMIT_COMPLETED;
    } catch (err) {
      if (err === operation || !isCurrentOperation()) {
        return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.STALE_SCOPE);
      }
      console.error('[DentallyBooking] Unexpected booking error at step', bookingStep?.index);
      const stepLabel = bookingStep?.label || 'booking';
      setError({ step: bookingStep?.index || 0, message: `Booking failed during "${stepLabel}". Please try again.` });
      return submitRejected(DENTALLY_BOOKING_SUBMIT_REASONS.UNEXPECTED_FAILURE);
    } finally {
      if (activeOperationRef.current === operation) {
        activeOperationRef.current = null;
        if (isCurrentOperation() || (mountedRef.current && scopeRef.current === operationScope)) {
          setLoading(false);
        }
      }
    }
  };

  return { loading, result, error, bookingStep, bookingDiagnostic, submit, reset };
}
